import { createStore, StoreCore } from "@priolo/jon"
import biApi from "api/bi"
import { ExportCSVData } from "components/controls/ExportDialog"
import { CHART_ZOOM_TYPES } from "components/pivotGrid/chart/types"
import { GridOptions, GridStats, Item, Property } from "components/pivotGrid/types"
import { exploreItems, updateTotals } from "components/pivotGrid/utils/items"
import { getStatsByData } from "components/pivotGrid/utils/stats"
import farmSo from "stores/farm"
import { DateRange } from "types/global"
import { BI_PRESETS } from "./types"
import { getPresetLibrary } from "./utils/presets"
import { getStructsByProperties } from "./utils/templates"



const setup = {

	state: {

		dataOrig: <any[]>null,
		data: <any[]>null,

		/** gli ITEM che componengono l'area ROWS | */
		rowsItems: <Item[]>null,
		/** gli ITEM che componengono l'area COLS - */
		colsItems: <Item[]>null,

		rowProperties: <Property[]>null,
		colProperties: <Property[]>null,
		valProperties: <Property[]>null,

		rowLibrary: <Property[]>null,
		colLibrary: <Property[]>null,
		valLibrary: <Property[]>null,

		/** indici ai DATA trovati nell'ultima ricerca */
		propertiesFound: <number[]>[],

		//** devo fare una STAT per ogni livello delle property */
		stats: <GridStats[]>[],
		/** opzioni per la visualizzazione della PIVOT GRID */
		options: <GridOptions>{
			cell: { width: 80, height: 20 },
			margin: { horiz: 5, vert: 10 },
			effect: "none"
		},

		cellSelected: <{ row: Item, col: Item }>{ row: null, col: null },

		/** se la DRAV delle opzioni è aperta/chiusa */
		isDrawerOpen: false,
		/** se il CHART è visibile */
		isChartVisible: false,
		chartZoomType: CHART_ZOOM_TYPES.XY,
		/** se la dialog per l'editazione della PROPERTY è aperto */
		isPropertyOpen: false,
		/** riferimento al menu contestuale della PROPERTY */
		refPropertyMenu: null,

		isCsvExportOpen: false,

		/** la PROPERTY in editazione (dialog e menu)*/
		propertyInEdit: <Property>null,

		tooltip: <Tooltip>{
			open: false,
			positionX: "right",
			positionY: "bottom",
		},
	},

	getters: {
		/** restituisco se la proprietà è "row" o "col" */
		getPropertyDirection: (property: Property, store?: BiStore) => store.state.rowProperties.some(p => p == property) ? "row" : "col",

		getAllLibrary: (_: void, store?: BiStore) => (store.state.rowLibrary ?? [])
			.concat(store.state.colLibrary ?? [])
			.concat(store.state.valLibrary ?? []),

		getPropertyLibraryByName: (name: string, store?: BiStore) => store.getAllLibrary().find(p => p.name == name),
	},

	actions: {

		fetch: async ({ range, preset, rejected }: FetchFilter, store?: BiStore) => {
			const farmId = farmSo.state.select?.id
			if (!preset) preset = BI_PRESETS.YIELD

			const data = await {
				[BI_PRESETS.YIELD]: biApi.indexYield,
				[BI_PRESETS.INCIDENTS]: biApi.indexIncidents,
				[BI_PRESETS.ALERTS]: biApi.indexAlerts,
			}[preset](farmId, range)
			store.state.dataOrig = data

			const library = getPresetLibrary(preset)
			store.state = { ...store.state, ...library }

			store.update()
		},

		/** eseguo un filtro sui dataOrig in base ai settaggi delle PROPRIETA'*/
		filter: (_: void, store?: BiStore) => {
			// prendo tutte le PROPERTY filtranti
			const properties = store.getAllLibrary()
				.filter(p => p.valueFilter != null && p.valueFilter.length > 0)
			// se non ci sono filtri, setto i dati originali
			if (properties.length == 0) {
				store.state.data = store.state.dataOrig
				// altrimenti filtro i dati
			} else {
				store.state.data = store.state.dataOrig.filter(item => {
					return properties.every(prop => {
						const value = item[prop.name]
						return prop.valueFilter.includes(value)
					})
				})
			}
		},

		/** update della PIVOT in base al settaggio in library */
		update: (_: void, store?: BiStore) => {
			store.filter()
			store.updateItems()
		},

		/** lascia visibili solo i dati selezionati */
		filterSelected: (_: void, store?: BiStore) => {
			const indexes = store.state.propertiesFound
			store.state.data = store.state.data.filter((_, index) => indexes.includes(index))
			store.setPropertiesFound([])
			store.updateItems()
		},

		/** deseleziona tutto */
		selectionReset: (_: void, store?: BiStore) => {
			store.setPropertiesFound([])
		},

		/** seleziona tutto */
		selectionAll: (_: void, store?: BiStore) => {
			store.setPropertiesFound(biSo.state.data.map((_, i) => i))
		},

		/** aggiorna tutta la baracca */
		updateItems: (_: void, store?: BiStore) => {
			// ricavo le strutture dell pivot grid e aggiorno lo STORE
			const str = getStructsByProperties(
				store.state.colLibrary,
				store.state.rowLibrary,
				store.state.valLibrary,
				store.state.data
			)
			store.state.rowProperties = str.rowProperties
			store.state.colProperties = str.colProperties
			store.state.colsItems = str.cols
			store.state.rowsItems = str.rows
			store.state.valProperties = str.valProperties

			// calcolo delle STATISTICHE
			const valProps = store.state.valProperties
			store.state.stats = valProps.map(type => getStatsByData(store.state.data, type.name))

			// aggiorno tutti i TOTALS degli ITEMS
			updateTotals(store.state.rowsItems, store.state.data, valProps)
			updateTotals(store.state.colsItems, store.state.data, valProps)

			store._update()
		},

		/** collassa/espande la PROPERTY in tutte le ITEM */
		collapse: (
			{ property, collapsed }: { property: Property, collapsed: boolean },
			store?: BiStore
		) => {
			const direction = store.getPropertyDirection(property)
			const items = direction == "row" ? store.state.rowsItems : store.state.colsItems
			const properties = direction == "row" ? store.state.rowProperties : store.state.colProperties

			const propDepth = properties.findIndex(p => p.name == property.name)
			if (propDepth == -1) return

			exploreItems(items, (item, depth) => {
				if (depth == propDepth) item.collapsed = collapsed
			})
			if (direction == "row") {
				store.setRowsItems([...items])
			} else {
				store.setColsItems([...items])
			}
		},

		/** per l'export in CSV */
		getExportData: (_: void, store?: BiStore): ExportCSVData => {
			const props = store.getAllLibrary()//.filter(p => p.visible)
			const headers = props.map(p => p.label)
			const description = props.map(p => p.description).filter(d => d)
			const items = store.state.data.reduce((acc, item) => {
				const value = props.map(prop => {
					const value = item[prop.name]
					return prop.renderCsv ? prop.renderCsv(value) : prop.render ? prop.render(value) : value
				})
				acc.push(value)
				return acc
			}, [])

			const farmName = farmSo.state.select.name
			return {
				headers,
				description,
				items,
				fileName: `YIELD-BI ${farmName}`
			}
		},
	},

	mutators: {
		setData: (data: any[]) => ({ data }),
		setRowsItems: (rowsItems: Item[]) => ({ rowsItems }),
		setColsItems: (colsItems: Item[]) => ({ colsItems }),
		setStats: (stats: GridStats[]) => ({ stats }),
		setOptions: (options: GridOptions) => ({ options }),
		setCellSelected: (cellSelected: { row: Item, col: Item }) => ({ cellSelected }),
		setPropertiesFound: (propertiesFound: number[]) => ({ propertiesFound }),

		setDrawerOpen: (isDrawerOpen: boolean) => ({ isDrawerOpen }),

		setChartVisible: (isChartVisible: boolean) => ({ isChartVisible }),
		setChartZoomType: (chartZoomType: CHART_ZOOM_TYPES) => ({ chartZoomType }),

		setPropertyOpen: (isPropertyOpen: boolean) => ({ isPropertyOpen }),
		setPropertyInEdit: (propertyInEdit: Property) => ({ propertyInEdit }),
		setRefPropertyMenu: (refPropertyMenu: any) => ({ refPropertyMenu }),

		setIsCsvExportOpen: (isCsvExportOpen: boolean) => ({ isCsvExportOpen }),

		setTooltip: (tooltip: Tooltip, store?: BiStore) => ({ tooltip: { ...store.state.tooltip, ...tooltip } })
	},
}



export type BiState = typeof setup.state
export type BiGetters = typeof setup.getters
export type BiActions = typeof setup.actions
export type BiMutators = typeof setup.mutators
export interface BiStore extends StoreCore<BiState>, BiGetters, BiActions, BiMutators {
	state: BiState
}
const biSo = createStore(setup) as BiStore
export default biSo

export type FetchFilter = { range?: DateRange, preset?: BI_PRESETS, rejected?: boolean }

export interface Tooltip {
	open?: boolean
	positionX?: "right" | "left"
	positionY?: "top" | "bottom"
	indexes?: number[]
}